import { consola } from 'consola'
import { useApi } from '@shared/api'

const logger = consola.withTag('api-notification-settings')

export interface NotificationSettings {
  enable_telegram_duplicate: boolean
  telegram_channel_mode: 'direct' | 'channel' | 'both'
  telegram_channel_id?: string
  notification_types: {
    duplicate_individual: boolean
    duplicate_broadcast: boolean
    duplicate_abandoned_cart: boolean
    duplicate_order_updates: boolean
    duplicate_promotional: boolean
  }
  telegram_format_template?: string
  use_telegram_markup: boolean
  quiet_hours: {
    respect_quiet_hours: boolean
    start: string
    end: string
    timezone: string
  }
  delay_telegram_seconds: number
  max_daily_telegram_notifications?: number
}

interface NotificationSettingsResponse {
  success: boolean
  settings: NotificationSettings
}

interface NotificationStatsResponse {
  success: boolean
  stats: {
    total_push_sent: number
    total_telegram_sent: number
    telegram_coverage_percent: number
    daily_stats: Array<{ date: string; push_sent: number; telegram_sent: number }>
    notification_types_stats: {
      individual: number
      broadcast: number
      abandoned_cart: number
      order_updates: number
      promotional: number
    }
  }
}

export interface TestNotificationRequest {
  user_id: number
  title: string
  body: string
  data?: Record<string, any>
}

export interface TestNotificationResponse {
  success: boolean
  message?: string
  result?: {
    success: boolean
    results: any[] | { push_sent?: boolean; telegram_sent?: boolean; message?: string }
    reason?: string
    error?: string | null
  }
  results?: { push_sent?: boolean; telegram_sent?: boolean; message?: string }
}

/**
 * API-клиент настроек уведомлений (admin).
 * Базовый URL: /api/v2/admin/notification-settings
 */
class APINotificationSettings {
  async getSettings(): Promise<NotificationSettings> {
    logger.debug('Fetching notification settings')
    const response = await useApi(true, true).get<NotificationSettingsResponse>('/admin/notification-settings')

    if (!response.data.success) throw new Error('Failed to fetch notification settings')

    logger.success('Notification settings fetched')
    return response.data.settings
  }

  async updateSettings(settings: Partial<NotificationSettings>): Promise<NotificationSettings> {
    logger.debug('Updating notification settings')
    const response = await useApi(true, true).put<NotificationSettingsResponse>(
      '/admin/notification-settings',
      { body: settings },
    )

    if (!response.data.success) throw new Error('Failed to update notification settings')

    logger.success('Notification settings updated')
    return response.data.settings
  }

  async sendTestNotification(data: TestNotificationRequest): Promise<TestNotificationResponse> {
    logger.debug('Sending test notification', { userId: data.user_id })
    const response = await useApi(true, true).post<TestNotificationResponse>(
      '/admin/notification-settings/test',
      { body: data },
    )
    logger.success('Test notification sent')
    return response.data
  }

  async getStats(days = 7): Promise<NotificationStatsResponse['stats']> {
    logger.debug('Fetching notification stats', { days })
    const response = await useApi(true, true).get<NotificationStatsResponse>(
      `/admin/notification-settings/stats?days=${days}`,
    )

    if (!response.data.success) throw new Error('Failed to fetch notification stats')

    logger.success('Notification stats fetched')
    return response.data.stats
  }
}

export const apiNotificationSettings = new APINotificationSettings()
export default apiNotificationSettings
